import {
    Box,
    Button,
    Grid,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Theme,
    Typography
} from "@mui/material";
import {
    AlphaIcon,
    BetaIcon,
    ChartIcon,
    CurrencyIcon,
    MoneyGrow,
    PercentageIcons,
    ProductsGainLossIcon,
} from "../../assets/SvgIcons";
import { IndNavBtnProps } from "../investorOverview/directEquity/types";
import CustomePieChart from "../dashboard/chart/CustomePieChart";
import CustomTable from "../investorOverview/common/customTable";

export function DropdownLoading(): JSX.Element {
    return (
        <Skeleton
            component="div"
            sx={{ borderRadius: '8px' }}
            animation="wave"
            height="80px"
        />
    )
}

function InfoCard({ Icon }: {
    Icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
}) {
    return (
        <Box
            sx={(theme: Theme) => ({
                backgroundColor: theme.palette.common.white,
                borderRadius: "15px",
                boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                padding: "25px 18px",
                display: "flex",
                height: "100%",
                alignItems: "center",
            })}
        >
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <Icon />
            </Box>
            <Box
                sx={{
                    flex: 3,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    rowGap: "8px",
                    width: '80%',
                }}
            >
                <Skeleton
                    animation="wave"
                    sx={{ width: '60%' }}
                />
                <Skeleton
                    animation="wave"
                    sx={{ width: '100%' }}
                />
            </Box>
        </Box>
    )
}

const infoIconsArray = [
    {
        Icon: (props: React.SVGProps<SVGSVGElement>) => <MoneyGrow {...props} viewBox="0 0 57 50" />,
    },
    {
        Icon: (props: React.SVGProps<SVGSVGElement>) => <CurrencyIcon {...props} />,
    },
    {
        Icon: (props: React.SVGProps<SVGSVGElement>) => <ChartIcon {...props} viewBox="0 0 57 50" />,
    },
    {
        Icon: (props: React.SVGProps<SVGSVGElement>) => <PercentageIcons {...props} viewBox="0 0 57 50" />,
    }
]

const productsIconsArray = [
    {
        Icon: (props: React.SVGProps<SVGSVGElement>) => <ProductsGainLossIcon {...props} viewBox="0 0 57 40" />
    },
    {
        Icon: (props: React.SVGProps<SVGSVGElement>) => <ChartIcon {...props} viewBox="0 0 57 40" />
    },
    {
        Icon: (props: React.SVGProps<SVGSVGElement>) => <AlphaIcon {...props} viewBox="0 0 57 40" />
    },
    {
        Icon: (props: React.SVGProps<SVGSVGElement>) => <BetaIcon {...props} viewBox="0 0 57 40" />
    }
]


export function InfoCardsLoading({ isProduct = false }: { isProduct?: boolean }): JSX.Element {
    const dataArray = isProduct ? productsIconsArray : infoIconsArray

    return (
        <>
            {dataArray.map((obj: { Icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element; }, index) => (
                <Grid
                    item
                    key={index}
                    xs={12}
                    md={6}
                    lg={3}
                >
                    <InfoCard
                        Icon={obj?.Icon}
                    />
                </Grid>
            ))}
        </>
    )
}

export const PortfolioAllocationLoading = (): JSX.Element => {
    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}
            >
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        lineHeight: "25px",
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })}
                >
                    Portfolio Allocation
                </Typography>
            </Box>
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                flexWrap: { xs: 'wrap', lg: 'nowrap' },
            }}>
                <Skeleton
                    variant="circular"
                    height={200}
                    sx={{
                        margin: "35px",
                        width: '100%',
                        maxWidth: '200px',
                    }} />
            </Box>
            <Grid
                container
                sx={{ paddingRight: "10px" }}
                rowSpacing={3}
            >
                {[...Array(5)].map((_, index) => (
                    <>
                        <Grid
                            key={index}
                            xs={6}
                            item
                            sx={{
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                columnGap: "15px",
                            }}
                        >
                            <Skeleton
                                variant="circular"
                                width={11}
                                height={11}
                                animation="wave"
                            />
                            <Skeleton
                                sx={{ fontSize: '16px' }}
                                animation="wave"
                                width={100}
                            />
                        </Grid>
                        <Grid
                            xs={6}
                            item
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                columnGap: "15px"
                            }}
                        >
                            <Skeleton
                                sx={{ fontSize: '16px' }}
                                animation="wave"
                                width={100}
                            />
                        </Grid>
                    </>
                ))}
            </Grid>
        </>
    )
}

const navChartButtons = [{
    type: 'month',
    count: 6,
    text: '6M'
}, {
    type: 'year',
    count: 1,
    text: '1Y'
},
{
    type: 'all',
    text: 'Inception'
}]

export const PortfolioHealthLoading = (): JSX.Element => {
    return (
        <>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                rowGap: '10px',
                mb: '55px'
            }}>
                <Typography
                    variant="h2"
                    color='text.primary'
                >
                    Portfolio Health
                </Typography>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: { sm: 'flex-end', md: 'flex-start' },
                    columnGap: 4,
                }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            columnGap: '7px'
                        }}>
                        <Skeleton
                            variant="circular"
                            width={11}
                            height={11}
                            animation="wave"
                        />
                        <Skeleton
                            animation="wave"
                            sx={{ fontSize: '14px' }}
                            width="4rem"
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            columnGap: '7px'
                        }}
                    >
                        <Skeleton
                            variant="circular"
                            width={11}
                            height={11}
                            animation="wave"
                        />
                        <Skeleton
                            animation="wave"
                            sx={{ fontSize: '14px' }}
                            width="5rem"
                        />
                    </Box>
                </Box>
            </Box>
            <Box>
                <Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Skeleton variant="rectangular" width={10} height={130} />
                        <Skeleton variant="rectangular" width={10} height={130} />
                        <Skeleton variant="rectangular" width={10} height={130} />
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                        <Skeleton variant="rectangular" width='100%' height={10} />
                    </Box>
                </Box>

                <Box
                    sx={{
                        mt: '45px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        columnGap: '10px'
                    }}
                >
                    {navChartButtons?.map((individualBtn: IndNavBtnProps, index: number) => {
                        return (
                            <Button
                                key={index}
                                sx={(theme: Theme) => ({
                                    ...theme.typography.body_500,
                                    letterSpacing: '0.02px',
                                    borderRadius: '7px',
                                    padding: '5px 15px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: { color: "Inception" === individualBtn?.text ? theme.palette.common.white : theme.palette.text.secondary },
                                    '&:hover': {
                                        border: 'none',
                                    }
                                }
                                )}
                                variant={"Inception" === individualBtn?.text ? 'contained' : 'outlined'}
                                disabled={true}
                            >
                                {individualBtn?.text}
                            </Button>
                        )
                    })}
                </Box>
            </Box>
        </>
    )
}

export function AssetAllocationLoading(): JSX.Element {
    return (
        <Box>
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
            }}>
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        lineheight: "25px",
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })}
                >
                    Asset Allocation
                </Typography>
            </Box>
            <Box sx={{ marginTop: "35px" }}>
                <CustomePieChart
                    data={[]}
                    isLoading={true}
                    labelData={[]}
                    totalValue={{}}
                    showSlider
                    align='center'
                />
            </Box>
        </Box>
    )
}

export function PortfolioExposureLoading(): JSX.Element {
    return (
        <Box>
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
            }}>
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        lineheight: "25px",
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })}
                >
                    Portfolio Exposure
                </Typography>
            </Box>
            <Box sx={{ marginTop: "35px" }}>
                <CustomePieChart
                    data={[]}
                    isLoading={true}
                    labelData={[]}
                    totalValue={{}}
                    showSlider
                    align='center'
                />
            </Box>
        </Box>
    )
}

export function HoldingsLoading(): JSX.Element {
    return (
        <Box>
            <Typography
                variant="h2"
                sx={(theme: Theme) => ({
                    lineheight: "25px",
                    letterSpacing: "0.01px",
                    color: theme.palette.text.primary
                })}
            >
                Holdings
            </Typography>
            <Skeleton
                variant="rounded"
                animation="wave"
                width="100%"
                height={48}
                sx={{
                    borderRadius: "25px",
                    marginTop: "35px"
                }}
            />
            <Box sx={{ marginTop: '45px' }}>
                <Box
                    sx={(theme: Theme) => ({
                        borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                        display: "flex",
                        columnGap: "6px"
                    })}>
                    {[...Array(2)].map((_, index) => (
                        <Skeleton
                            key={index}
                            component="div"
                            variant="rectangular"
                            animation="wave"
                            width={60}
                            height={25}
                            sx={{
                                borderRadius: '7px 7px 0 0',
                            }}
                        />
                    ))}
                </Box>
            </Box>
            <Box sx={{ marginTop: "45px" }}>
                <TableContainer sx={{ maxHeight: "400px" }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell />
                                <TableCell sx={{ padding: 0 }}>
                                    <Skeleton
                                        animation="wave"
                                        width="50%"
                                        sx={(theme: Theme) => ({
                                            fontSize: theme.typography.subtitle_500.fontSize
                                        })}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Skeleton
                                        animation="wave"
                                        width="50%"
                                        sx={(theme: Theme) => ({
                                            fontSize: theme.typography.subtitle_500.fontSize
                                        })}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Skeleton
                                        animation="wave"
                                        width="50%"
                                        sx={(theme: Theme) => ({
                                            fontSize: theme.typography.subtitle_500.fontSize
                                        })}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[...Array(5)].map((_, index) => (
                                <TableRow key={index}>
                                    <TableCell sx={{ width: '20%' }}>
                                        <Skeleton
                                            animation="wave"
                                            sx={(theme: Theme) => ({
                                                fontSize: theme.typography.body_500.fontSize
                                            })}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ padding: 0, width: '20%' }}>
                                        <Skeleton
                                            animation="wave"
                                            sx={(theme: Theme) => ({
                                                fontSize: theme.typography.body_500.fontSize
                                            })}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton
                                            animation="wave"
                                            sx={(theme: Theme) => ({
                                                fontSize: theme.typography.body_500.fontSize
                                            })}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ width: '50%' }}>
                                        <Skeleton
                                            animation="wave"
                                            sx={(theme: Theme) => ({
                                                fontSize: theme.typography.body_500.fontSize
                                            })}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer >
            </Box>
        </Box>
    )
}

export function HoldingPeriodAnalysisLoading(): JSX.Element {
    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column"
            }}>
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
            }}>
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        lineheight: "25px",
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })}
                >
                    Holding Period Analysis
                </Typography>
            </Box>
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: { xs: 'column', lg: 'row' },
                    columnGap: { xs: "0px", lg: "25px" },
                    rowGap: { xs: "35px", lg: "0px" },
                    marginTop: "35px",
                    justifyContent: "center",
                    alignItems: "center"
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: "column",
                        rowGap: "20px",
                        height: "100%"
                    }}
                >
                    <Skeleton
                        component="div"
                        variant="rounded"
                        animation="wave"
                        height="50%"
                        sx={{ borderRadius: "50%" }}
                    />
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            flexDirection: 'column',
                            rowGap: "10px"
                        }}
                    >
                        {[...Array(2)].map((_, index) => (
                            <Grid
                                key={index}
                                container
                                columnSpacing={2}
                                sx={{ justifyContent: "center" }}
                            >
                                <Grid
                                    item
                                    xs={2}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center"
                                    }}
                                >
                                    <Skeleton
                                        variant="rounded"
                                        animation="wave"
                                        height="11.69px"
                                        width="11.69px"
                                        sx={{ borderRadius: "50%" }}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <Skeleton
                                        animation="wave"
                                        sx={(theme: Theme) => ({
                                            fontSize: theme.typography.subtitle_400.fontSize,
                                        })}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Skeleton
                                        animation="wave"
                                        sx={(theme: Theme) => ({
                                            fontSize: theme.typography.subtitle_400.fontSize,
                                        })}
                                    />
                                </Grid>
                            </Grid>
                        ))}
                    </Box>
                </Box>
                <Box sx={{ flex: 1.2 }}>
                    <Box
                        sx={(theme: Theme) => ({
                            paddingBottom: '20px',
                            borderBottom: `0.5px solid ${theme.palette.customColors.pastelGrey}`
                        })}
                    >
                        <Skeleton
                            component="div"
                            animation="wave"
                            width="50%"
                            sx={(theme: Theme) => ({
                                fontSize: theme.typography.subtitle_400.fontSize,
                            })}
                        />
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "flex-start",
                                rowGap: "10px",
                                marginTop: "18px"
                            }}
                        >
                            <Skeleton
                                animation="wave"
                                width="30%"
                                sx={(theme: Theme) => ({
                                    fontSize: theme.typography.overline_400.fontSize,
                                })}
                            />
                            <Skeleton
                                animation="wave"
                                width="30%"
                                sx={(theme: Theme) => ({
                                    fontSize: theme.typography.overline_400.fontSize,
                                })}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ paddingTop: '20px', }}>
                        <Skeleton
                            component="div"
                            animation="wave"
                            width="50%"
                            sx={(theme: Theme) => ({
                                fontSize: theme.typography.subtitle_400.fontSize,
                            })}
                        />
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "flex-start",
                                rowGap: "10px",
                                marginTop: "18px"
                            }}
                        >
                            <Skeleton
                                animation="wave"
                                width="30%"
                                sx={(theme: Theme) => ({
                                    fontSize: theme.typography.overline_400.fontSize,
                                })}
                            />
                            <Skeleton
                                animation="wave"
                                width="30%"
                                sx={(theme: Theme) => ({
                                    fontSize: theme.typography.overline_400.fontSize,
                                })}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export function MarketCapAllocationLoading(): JSX.Element {
    return (
        <Box>
            <Typography
                variant="h2"
                sx={(theme: Theme) => ({
                    lineheight: "25px",
                    letterSpacing: "0.01px",
                    color: theme.palette.text.primary
                })}
            >
                Market Cap Allocation
            </Typography>
            <Box
                sx={{
                    marginTop: "26px",
                    display: "flex",
                    columnGap: "50px",
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        height: "42px",
                    }}
                >
                    <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width="100%"
                        height="100%"
                        sx={{ borderRadius: "7px" }}
                    />
                </Box>
                <Grid
                    container
                    sx={{
                        flex: 1,
                        alignItems: "center",
                        flexWrap: "wrap"
                    }}
                >
                    {[...Array(4)].map((_, index) => (
                        <Grid
                            key={index}
                            xs={12}
                            md={6}
                            lg
                            item
                            container
                            sx={{
                                flexWrap: "nowrap",
                                columnGap: "10px",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <Grid item xs="auto">
                                <Skeleton
                                    animation="wave"
                                    variant="rounded"
                                    width='8.79px'
                                    height='8.79px'
                                    sx={{ borderRadius: "50%" }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <Skeleton
                                    width="100%"
                                    animation="wave"
                                    sx={(theme: Theme) => ({
                                        fontSize: theme.typography.overline_400.fontSize
                                    })}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <Skeleton
                                    width="100%"
                                    animation="wave"
                                    sx={(theme: Theme) => ({
                                        fontSize: theme.typography.overline_400.fontSize
                                    })}
                                />
                            </Grid>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Box >
    )
}

export function AmcPerformanceAnalysisLoading(): JSX.Element {
    return (
        <Box>
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
            }}>
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        lineheight: "25px",
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })}
                >
                    AMC Performance Analysis
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        columnGap: "25px",
                        flexWrap: "nowrap",
                        justifyContent: "center",
                        alignItems: "center"
                    }}
                >
                    <Skeleton
                        animation="wave"
                        variant="rectangular"
                        width={115}
                        height={36}
                    />

                </Box>
            </Box >
            <Box sx={{ marginTop: "20px" }}>
                <CustomTable
                    isLoading={true}
                    tableHeaderData={[...Array(9)]}
                    tableBodyLoaderRowCustomStyles={{
                        '& td:first-of-type': {
                            paddingLeft: "0px"
                        },
                    }}
                    tableHeadRowCustomStyles={{
                        '& th:first-of-type': {
                            paddingLeft: "0px"
                        }
                    }}
                    loadingRowsNum={5}
                />
            </Box>
        </Box >
    )
}

export function SchemewiseAnalysisLoading(): JSX.Element {
    return (
        <Box>
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
            }}>
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        lineheight: "25px",
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })}
                >
                    Schemewise Analysis
                </Typography>
            </Box >
            <Box sx={{ marginTop: "20px" }}>
                <CustomTable
                    isLoading={true}
                    tableHeaderData={[...Array(5)]}
                    tableBodyLoaderRowCustomStyles={{
                        '& td:first-of-type': {
                            paddingLeft: "0px"
                        },
                    }}
                    tableHeadRowCustomStyles={{
                        '& th:first-of-type': {
                            paddingLeft: "0px"
                        }
                    }}
                    loadingRowsNum={5}
                />
            </Box>
        </Box >
    )
}

export function DescriptiveAnalysisLoading(): JSX.Element {
    return (
        <>
            <Grid
                container
                spacing={2}
            >
                <InfoCardsLoading />
            </Grid>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: 'column', lg: 'row' },
                    columnGap: { xs: "0px", lg: "20px" },
                    rowGap: { xs: "35px", lg: "0px" },
                    marginTop: "35px"
                }}
            >
                <Box
                    sx={(theme: Theme) => ({
                        flex: { xs: 1, lg: 1.5 },
                        backgroundColor: theme.palette.common.white,
                        borderRadius: "20px",
                        padding: "30px",
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                    })}
                >
                    <PortfolioAllocationLoading />
                </Box>
                <Box
                    sx={(theme: Theme) => ({
                        flex: { xs: 1, lg: 2.5 },
                        borderRadius: "25px",
                        padding: "30px",
                        backgroundColor: theme.palette.common.white,
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                    })}
                >
                    <PortfolioHealthLoading />
                </Box>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: 'column', lg: 'row' },
                    columnGap: { xs: "0px", lg: "20px" },
                    rowGap: { xs: "35px", lg: "0px" },
                    marginTop: "35px"
                }}
            >
                <Box
                    sx={(theme: Theme) => ({
                        flex: 1,
                        backgroundColor: theme.palette.common.white,
                        borderRadius: "20px",
                        padding: "30px",
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                    })}
                >
                    <AssetAllocationLoading />
                </Box>
                <Box
                    sx={(theme: Theme) => ({
                        flex: 1,
                        borderRadius: "25px",
                        padding: "30px",
                        backgroundColor: theme.palette.common.white,
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                    })}
                >
                    <PortfolioExposureLoading />
                </Box>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: 'column', lg: 'row' },
                    columnGap: { xs: "0px", lg: "20px" },
                    rowGap: { xs: "35px", lg: "0px" },
                    marginTop: "35px"
                }}
            >
                <Box
                    sx={(theme: Theme) => ({
                        flex: 1,
                        backgroundColor: theme.palette.common.white,
                        borderRadius: "20px",
                        padding: "30px",
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                    })}
                >
                    <HoldingsLoading />
                </Box>
                <Box
                    sx={(theme: Theme) => ({
                        flex: 1,
                        borderRadius: "25px",
                        padding: "30px",
                        backgroundColor: theme.palette.common.white,
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    })}
                >
                    <HoldingPeriodAnalysisLoading />
                </Box>
            </Box>
            <>
                <Box
                    sx={(theme: Theme) => ({
                        display: 'flex',
                        gap: '10px',
                        paddingBottom: '15px',
                        borderBottom: `1px solid ${theme.palette.customColors.pastelGrey}`,
                        overflowX: 'auto',
                        marginTop: "65px"
                    })}
                >
                    {[
                        "Mutual Fund",
                        "Direct Equity",
                        "PMS",
                        "Bonds",
                        "Alternates"
                    ].map((item) => {
                        return (
                            <Button
                                variant={"Mutual Fund" === item ? "contained" : "text"}
                                key={item}
                                onClick={() => { return; }}
                                sx={(theme: Theme) => ({
                                    padding: '0px 15px',
                                    background: "Mutual Fund" === item ? theme.palette.primary[500] : 'transparent',
                                    borderRadius: '7px',
                                    outline: 'none',
                                    border: 'none',
                                    cursor: 'pointer'
                                })}
                                disabled
                            >
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        color: "Mutual Fund" === item ? theme.palette.textDark.primary : theme.palette.text.secondary
                                    })}
                                >
                                    {item}
                                </Typography>
                            </Button>
                        )
                    })}
                </Box>

                <Box
                    sx={(theme: Theme) => ({
                        backgroundColor: theme.palette.common.white,
                        borderRadius: "20px",
                        padding: "30px",
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                        marginTop: "36px"
                    })}
                >
                    <MarketCapAllocationLoading />
                </Box>

                <Box
                    sx={(theme: Theme) => ({
                        backgroundColor: theme.palette.common.white,
                        borderRadius: "20px",
                        padding: "30px",
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                        marginTop: "36px"
                    })}
                >
                    <AmcPerformanceAnalysisLoading />
                </Box>

                <Grid
                    container
                    spacing={2}
                    sx={{ marginTop: '32px' }}
                >
                    <InfoCardsLoading isProduct />
                </Grid>

                <Box
                    sx={(theme: Theme) => ({
                        backgroundColor: theme.palette.common.white,
                        borderRadius: "20px",
                        padding: "30px",
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                        marginTop: "36px"
                    })}
                >
                    <SchemewiseAnalysisLoading />
                </Box>

            </>
        </>
    )
}